import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {number} Timestamp when submenu was last opened via hover */
  #lastHoverActivateTime = 0;

  connectedCallback() {
    super.connectedCallback();

    // Apply skip-node-update to all links so DOM hydration doesn't detach listeners
    this.querySelectorAll('a').forEach(link => {
      link.setAttribute('data-skip-node-update', 'true');
    });

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('pointerdown', this.#closeIfPointerOutsideHeader, true);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerdown', this.#forceSubmenuPointerNavigation, true);
    this.addEventListener('click', this.#preventParentWithSubmenuNavigate, true);
    this.addEventListener('click', this.#forceSubmenuLinkNavigation, true);
    this.addEventListener('keydown', this.#preventParentWithSubmenuKeydown, true);
    this.addEventListener('pointerenter', this.#onPointerEnterKeepOpen, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('pointerdown', this.#closeIfPointerOutsideHeader, true);
    this.removeEventListener('pointerdown', this.#forceSubmenuPointerNavigation, true);
    this.removeEventListener('click', this.#preventParentWithSubmenuNavigate, true);
    this.removeEventListener('click', this.#forceSubmenuLinkNavigation, true);
    this.removeEventListener('keydown', this.#preventParentWithSubmenuKeydown, true);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerenter', this.#onPointerEnterKeepOpen, true);
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * After touch/pen we ignore pointerleave; close when user taps outside the header.
   * @param {PointerEvent} event
   */
  #closeIfPointerOutsideHeader = (event) => {
    if (!this.#state.activeItem) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    const header = this.headerComponent;
    if (!header?.contains(target)) {
      this.#deactivate(this.#state.activeItem, true);
      return;
    }
    // If the pointerdown lands inside the open submenu panel, record it so the
    // pointerleave that fires at the same time does not close the drawer.
    const activeSubmenu = findSubmenu(this.#state.activeItem);
    if (activeSubmenu?.contains(target)) {
      this.#lastSubmenuPointerdownTime = performance.now();
      this.#cancelScheduledPointerLeaveClose();
    }
  };

  /**
   * Returns true for genuine touch/pen events OR a coarse-primary-pointer device
   * (phone, tablet). Uses ONLY matchMedia so a desktop mouse on a touch-capable
   * laptop is never mis-classified as coarse.
   * Used by: activate (hover guard) and deactivate (pointerleave guard).
   * @param {string} [pointerType]
   * @returns {boolean}
   */
  #isCoarsePointerType(pointerType) {
    if (pointerType === 'touch' || pointerType === 'pen') return true;
    return window.matchMedia?.('(pointer: coarse)').matches === true;
  }

  /**
   * Returns true when a click/tap came from a touch-like interaction.
   * Broader than #isCoarsePointerType: also catches Windows/Android tablets
   * that report pointerType 'mouse' for touch events (maxTouchPoints > 0).
   * Used by: the click handler only.
   * @param {string} [pointerType]
   * @returns {boolean}
   */
  #isTabletClick(pointerType) {
    if (pointerType === 'touch' || pointerType === 'pen') return true;
    return (
      window.matchMedia?.('(pointer: coarse)').matches === true ||
      (navigator.maxTouchPoints > 0 && pointerType === 'mouse')
    );
  }

  /**
   * Top-level items that have a mega-menu should not navigate; hover/focus opens the submenu only.
   * Modifier+click still allows opening the URL in a new tab.
   * @param {MouseEvent} event
   */
  #preventParentWithSubmenuNavigate = (event) => {
    if (!(event instanceof MouseEvent)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== 0) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a.menu-list__link[aria-haspopup="true"]');
    if (!link || !this.contains(link)) return;

    const menuItemEl = link.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
    if (!(menuItemEl instanceof HTMLElement)) return;

    // Detect pointer type from the click event (PointerEvent extends MouseEvent)
    const pointerType =
      'pointerType' in event && typeof event.pointerType === 'string' ? event.pointerType : 'mouse';
    const isTablet = this.#isTabletClick(pointerType);
    const isOpen = (menuItemEl === this.#state.activeItem);

    if (isTablet) {
      // Tablet/touch behavior: parent item with submenu should never navigate.
      // Tap only opens (or keeps open) the submenu.
      event.preventDefault();
      this.#cancelScheduledPointerLeaveClose();
      if (!isOpen) {
        this.#activateItem(menuItemEl);
      }
    } else {
      // Desktop: mouse clicks. Allow navigation, so do NOT prevent default.
      // But we prevent default if it's just a dummy link (#) and we want it to toggle.
      const href = link.getAttribute('href');
      if (!href || href === '#' || href === '') {
        event.preventDefault();
        this.#cancelScheduledPointerLeaveClose();
        if (isOpen) {
          if (performance.now() - this.#lastHoverActivateTime >= 300) {
            this.#deactivate(menuItemEl, true);
          }
        } else {
          this.#activateItem(menuItemEl);
        }
      }
    }
  };

  /**
   * Fallback for desktop submenu links: force navigation on normal left click.
   * This prevents race conditions where menu-closing side effects swallow redirect.
   * @param {MouseEvent} event
   */
  #forceSubmenuLinkNavigation = (event) => {
    if (!(event instanceof MouseEvent)) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('.menu-list__submenu a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') {
      event.preventDefault();
      return;
    }

    // Keep _blank/new-tab behavior native.
    if (link.target === '_blank') return;

    event.preventDefault();
    event.stopPropagation();
    window.location.assign(link.href);
  };

  /**
   * Early navigation fallback for desktop submenu links.
   * Runs on pointerdown so menu close/hover handlers cannot swallow click navigation.
   * @param {PointerEvent} event
   */
  #forceSubmenuPointerNavigation = (event) => {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType !== 'mouse') return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('.menu-list__submenu a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') {
      event.preventDefault();
      return;
    }

    if (link.target === '_blank') return;

    event.preventDefault();
    event.stopPropagation();
    window.location.assign(link.href);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #preventParentWithSubmenuKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a.menu-list__link[aria-haspopup="true"]');
    if (!link || !this.contains(link)) return;
    event.preventDefault();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * When the submenu was last opened (for click vs pointerenter ordering on mouse).
   * @type {number}
   */
  #lastSubmenuOpenTime = 0;

  /**
   * Delayed close from pointerleave so the cursor can cross the gap into the mega panel.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #pointerLeaveCloseTimer = null;

  /**
   * Timestamp of the last pointerdown inside the currently open submenu panel.
   * Used to suppress the pointerleave-triggered close that fires ~same time as the click.
   * @type {number}
   */
  #lastSubmenuPointerdownTime = 0;

  /**
   * Returns true if `element` is inside the currently open submenu panel.
   * Used to avoid closing on pointerenter of submenu children.
   * @param {Element} element
   * @returns {boolean}
   */
  #isInsideActiveSubmenu(element) {
    if (!this.#state.activeItem) return false;
    const submenu = findSubmenu(this.#state.activeItem);
    return Boolean(submenu?.contains(element));
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerEnterKeepOpen = (event) => {
    if (!(event.target instanceof Element)) return;

    // If entering the open submenu panel itself OR any element inside it,
    // always cancel the close timer regardless of pointer type.
    if (
      event.target.closest('.menu-list__submenu') ||
      this.#isInsideActiveSubmenu(event.target)
    ) {
      this.#cancelScheduledPointerLeaveClose();
      return;
    }

    // Below logic is mouse-only (hover intent). Touch/coarse devices open
    // exclusively via click, so skip the schedule/cancel logic for them.
    if (this.#isCoarsePointerType(event.pointerType)) return;

    // If entering a top-level list item, only cancel the close timer when that
    // item actually owns a submenu. Otherwise close IMMEDIATELY so the drawer
    // doesn't linger while hovering a plain link.
    const listItem = event.target.closest('.menu-list__list-item');
    if (listItem) {
      const hasSubmenu = Boolean(listItem.querySelector('[ref="submenu[]"]'));
      if (hasSubmenu) {
        this.#cancelScheduledPointerLeaveClose();
      } else if (this.#state.activeItem) {
        // Plain link hovered while a mega menu is open → close immediately.
        this.#cancelScheduledPointerLeaveClose();
        this.#deactivate();
      }
    }
  };

  #cancelScheduledPointerLeaveClose = () => {
    if (this.#pointerLeaveCloseTimer !== null) {
      clearTimeout(this.#pointerLeaveCloseTimer);
      this.#pointerLeaveCloseTimer = null;
    }
  };

  #schedulePointerLeaveClose = () => {
    this.#cancelScheduledPointerLeaveClose();
    this.#pointerLeaveCloseTimer = setTimeout(() => {
      this.#pointerLeaveCloseTimer = null;
      this.#deactivate();
    }, 650);
  };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Internal core activation logic — shared by hover (activate) and click (#preventParentWithSubmenuNavigate).
   * @param {HTMLElement} item - element with [ref="menuitem"]
   */
  #activateItem(item) {
    if (!this.headerComponent) return;

    const listItem = item.closest('.menu-list__list-item');
    const inOverflowSlot = listItem?.getAttribute('slot') === 'overflow';

    let submenu = findSubmenu(item);
    if (!submenu && inOverflowSlot) {
      submenu = this.overflowMenu;
    }

    // Plain top-level link (no mega / overflow panel): do not expand header or set active state.
    if (!submenu) return;

    this.#cancelScheduledPointerLeaveClose();
    this.dispatchEvent(new MegaMenuHoverEvent());

    // Already the active item — toggle closed (handles hover sticky on hybrid devices too)
    if (item === this.#state.activeItem) {
      this.#deactivate(item, true);
      return;
    }

    this.dataset.overflowExpanded = inOverflowSlot ? 'true' : 'false';

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      previouslyActiveItem.setAttribute('aria-expanded', 'false');
      this.#clearSubmenuOpenClasses(previouslyActiveItem);
    }

    this.#state.activeItem = item;
    this.#lastHoverActivateTime = performance.now();
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    item.setAttribute('aria-expanded', 'true');
    this.#setSubmenuOpenClasses(item, true);

    // Mark submenu as active for content-visibility optimization
    submenu.dataset.active = '';

    // Cleanup any existing mutation observer from previous menu activations
    this.#cleanupMutationObserver();

    // Monitor DOM mutations to catch deferred content injection (from section hydration)
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        // Double requestAnimationFrame to ensure the height is properly calculated
        // and not defaulting to the contain-intrinsic-size
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#cleanupMutationObserver();
          }
        });
      });
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

    // Auto-disconnect after 500ms to prevent memory leaks
    setTimeout(() => {
      this.#cleanupMutationObserver();
    }, 500);

    const hasOwnSubmenu = Boolean(findSubmenu(item));

    let finalHeight = submenu.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (inOverflowSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasOwnSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#lastSubmenuOpenTime = performance.now();
    this.#startPointerTracking(item, previouslyActiveItem);
  }

  /**
   * Activate the selected menu item immediately (called from pointerenter / focus events).
   * Touch and coarse-pointer devices are handled exclusively by click.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // Touch/pen/coarse devices open exclusively via click — ignore pointerenter.
    // Uses matchMedia only (not maxTouchPoints) so a real desktop mouse on a
    // touch-capable laptop still opens the menu on hover.
    if (event instanceof PointerEvent) {
      const pt = event.pointerType;
      if (pt === 'touch' || pt === 'pen') return;
      if (pt === 'mouse' && window.matchMedia?.('(pointer: coarse)').matches) return;
    }

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#activateItem(item);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Tablet/touch: lifting the finger fires pointerleave — ignore it so the
    // menu stays open after a tap. Only skip genuine touch/pen here; real
    // desktop mouse pointerleave still schedules the gap-crossing close timer.
    if (event.type === 'pointerleave' && event instanceof PointerEvent) {
      if (event.pointerType === 'touch' || event.pointerType === 'pen') {
        return;
      }
      // Pointer is still inside header-menu/submenu area; do not begin closing.
      if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) {
        return;
      }
      // If a pointerdown just happened inside the submenu (< 300ms ago),
      // the user is clicking a submenu link — do not schedule a close.
      if (performance.now() - this.#lastSubmenuPointerdownTime < 300) {
        return;
      }
      // Mouse: defer close so the pointer can cross layout gaps into the mega panel.
      this.#schedulePointerLeaveClose();
      return;
    }

    this.#cancelScheduledPointerLeaveClose();

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      event.type === 'blur' &&
      // relatedTarget is inside the submenu panel OR anywhere inside this header-menu
      // (covers mega-menu links that are siblings/cousins of the menuitem in the DOM).
      (menu?.contains(event.relatedTarget) || this.contains(/** @type {Node} */(event.relatedTarget)));
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - Bypass hover guards (e.g. same-item toggle / touch where :hover can stick)
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    this.#cancelScheduledPointerLeaveClose();

    if (
      !force &&
      (this.overflowListHovered || this.overflowMenu?.matches(':hover'))
    ) {
      return;
    }

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('aria-expanded', 'false');
    this.#setSubmenuOpenClasses(item, false);

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * @param {HTMLElement} menuItemLink - element with [ref="menuitem"]
   * @param {boolean} open
   */
  #setSubmenuOpenClasses(menuItemLink, open) {
    const li = menuItemLink.closest('.menu-list__list-item');
    if (open) {
      li?.classList.add('menu-list__list-item--submenu-open');
      menuItemLink.classList.add('menu-list__link--submenu-open');
    } else {
      li?.classList.remove('menu-list__list-item--submenu-open');
      menuItemLink.classList.remove('menu-list__link--submenu-open');
    }
  }

  /**
   * @param {HTMLElement} menuItemLink
   */
  #clearSubmenuOpenClasses(menuItemLink) {
    this.#setSubmenuOpenClasses(menuItemLink, false);
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]') ?? null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}